import { getErrorCode } from "@/lib/constants/error-codes"

export type AuthFlowErrorParts = { title: string; description: string }

type ClerkErrorEntry = {
  code?: string
  longMessage?: string
  message?: string
}

type TranslateFn = (key: string) => string

const CLERK_KEYS: Record<string, { titleKey: string; descriptionKey: string }> =
  {
    form_identifier_not_found: {
      titleKey: "auth.emailAuth.clerkErrors.form_identifier_not_found.title",
      descriptionKey:
        "auth.emailAuth.clerkErrors.form_identifier_not_found.description",
    },
    form_identifier_exists: {
      titleKey: "auth.emailAuth.clerkErrors.form_identifier_exists.title",
      descriptionKey:
        "auth.emailAuth.clerkErrors.form_identifier_exists.description",
    },
    identifier_exists: {
      titleKey: "auth.emailAuth.clerkErrors.identifier_exists.title",
      descriptionKey:
        "auth.emailAuth.clerkErrors.identifier_exists.description",
    },
    form_param_format_invalid: {
      titleKey: "auth.emailAuth.clerkErrors.form_param_format_invalid.title",
      descriptionKey:
        "auth.emailAuth.clerkErrors.form_param_format_invalid.description",
    },
    form_param_missing: {
      titleKey: "auth.emailAuth.clerkErrors.form_param_missing.title",
      descriptionKey:
        "auth.emailAuth.clerkErrors.form_param_missing.description",
    },
    verification_failed: {
      titleKey: "auth.emailAuth.clerkErrors.verification_failed.title",
      descriptionKey:
        "auth.emailAuth.clerkErrors.verification_failed.description",
    },
    verification_expired: {
      titleKey: "auth.emailAuth.clerkErrors.verification_expired.title",
      descriptionKey:
        "auth.emailAuth.clerkErrors.verification_expired.description",
    },
    session_exists: {
      titleKey: "auth.emailAuth.clerkErrors.session_exists.title",
      descriptionKey: "auth.emailAuth.clerkErrors.session_exists.description",
    },
    rate_limit_exceeded: {
      titleKey: "auth.emailAuth.clerkErrors.rate_limit_exceeded.title",
      descriptionKey:
        "auth.emailAuth.clerkErrors.rate_limit_exceeded.description",
    },
    too_many_requests: {
      titleKey: "auth.emailAuth.clerkErrors.too_many_requests.title",
      descriptionKey:
        "auth.emailAuth.clerkErrors.too_many_requests.description",
    },
    signup_restricted: {
      titleKey: "auth.emailAuth.clerkErrors.signup_restricted.title",
      descriptionKey:
        "auth.emailAuth.clerkErrors.signup_restricted.description",
    },
    bot_sign_up_blocked: {
      titleKey: "auth.emailAuth.clerkErrors.bot_sign_up_blocked.title",
      descriptionKey:
        "auth.emailAuth.clerkErrors.bot_sign_up_blocked.description",
    },
  }

const INTERNAL_THROWN_MESSAGES: Record<
  string,
  { titleKey: string; descriptionKey: string }
> = {
  "Email sign in is not available for this account.": {
    titleKey: "auth.emailAuth.fallbacks.signIn.emailFactorMissing.title",
    descriptionKey:
      "auth.emailAuth.fallbacks.signIn.emailFactorMissing.description",
  },
  "Email verification is incomplete.": {
    titleKey: "auth.emailAuth.fallbacks.verificationIncomplete.title",
    descriptionKey:
      "auth.emailAuth.fallbacks.verificationIncomplete.description",
  },
  "Sign-up role is required.": {
    titleKey: "auth.emailAuth.fallbacks.signUp.roleRequired.title",
    descriptionKey: "auth.emailAuth.fallbacks.signUp.roleRequired.description",
  },
}

function getFirstApiErrorCode(error: unknown): number | undefined {
  const e = error as {
    data?: { detail?: { codes?: number[] } }
    detail?: { codes?: number[] }
  }
  const code = e?.data?.detail?.codes?.[0] ?? e?.detail?.codes?.[0]
  if (typeof code === "number") return code
  return undefined
}

function getFirstClerkError(error: unknown): ClerkErrorEntry | undefined {
  return (error as { errors?: ClerkErrorEntry[] })?.errors?.[0]
}

/**
 * Maps backend API codes, Clerk auth errors, and thrown messages to user-facing
 * title + description for login/sign-up toasts.
 */
export function resolveAuthFlowError(
  error: unknown,
  fallback: AuthFlowErrorParts,
  t: TranslateFn
): AuthFlowErrorParts {
  const apiCode = getFirstApiErrorCode(error)
  if (apiCode !== undefined) {
    return getErrorCode(apiCode)
  }

  const thrown = error instanceof Error ? error.message : undefined
  if (thrown && INTERNAL_THROWN_MESSAGES[thrown]) {
    const keys = INTERNAL_THROWN_MESSAGES[thrown]
    return {
      title: t(keys.titleKey),
      description: t(keys.descriptionKey),
    }
  }

  const clerk = getFirstClerkError(error)
  if (clerk?.code) {
    const mapped = CLERK_KEYS[clerk.code]
    if (mapped) {
      return {
        title: t(mapped.titleKey),
        description: t(mapped.descriptionKey),
      }
    }
  }

  const detail = clerk?.longMessage || clerk?.message || thrown

  if (detail) {
    return {
      title: t("auth.emailAuth.clerkErrors.genericWithDetail.title"),
      description: detail,
    }
  }

  return fallback
}
